import { 
  Save, Eye, Undo2, Redo2, Loader2, CheckCircle, X, ArrowLeft, 
  Trash2, Globe, Languages, PanelRight, PanelRightClose,
  Settings, FileEdit, MoreVertical, ChevronDown
} from "lucide-react"
import { Button } from "../ui/button"

export default function EditorHeader({
  course,
  onSave,
  onPreview,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  isSaving,
  saveStatus,
  onQuit,
  onDeleteCourse,
  onStatusChange,
  onTogglePublished,
  isPublishing = false,
  isCreatingNew = false,
  onShowTranslations,
  showInspector = true,
  onToggleInspector
}) {
  const title = course?.title || 'Sans titre'

  return (
    <header className="h-12 bg-white border-b border-gray-200 flex items-center justify-between px-4 shadow-sm z-10">
      {/* Left: Course Title & Status */}
      <div className="flex-1 flex items-center gap-3 min-w-0">
        {/* Back Button */}
        {onQuit && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onQuit}
            className="h-10 w-10 p-0 text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            title="Retour"
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>
        )}

        {/* Divider */}
        <div className="h-6 w-px bg-gray-300" />

        {/* Course Title */}
        <h1 className="text-sm font-semibold text-gray-900 px-2 py-1 truncate">
          {title}
        </h1>
        
        {/* Save Status Indicator - Icon only */}
        {saveStatus === 'saving' && (
          <div className="flex items-center" title="Sauvegarde en cours...">
            <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
          </div>
        )}
        {saveStatus === 'saved' && (
          <div className="flex items-center" title="Sauvegardé">
            <CheckCircle className="w-6 h-6 text-green-600" />
          </div>
        )}
        {saveStatus === 'error' && (
          <div className="flex items-center" title="Erreur de sauvegarde">
            <X className="w-6 h-6 text-red-600" />
          </div>
        )}
      </div>

      {/* Center: Main Actions */}
      <div className="flex items-center gap-1">
        {/* Save */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onSave}
          disabled={isSaving}
          className="h-10 w-10 p-0 text-gray-600 hover:text-gray-900 hover:bg-gray-100"
          title="Sauvegarder (Ctrl+S)"
        >
          {isSaving ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : (
            <Save className="w-6 h-6" />
          )}
        </Button>
      </div>

      {/* Right: Secondary Actions & Settings */}
      <div className="flex items-center gap-1">
        {/* Toggle Inspector */}
        {onToggleInspector && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleInspector}
            className="h-10 w-10 p-0 text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            title={showInspector ? "Masquer l'inspecteur" : "Afficher l'inspecteur"}
          >
            {showInspector ? (
              <PanelRightClose className="w-6 h-6" />
            ) : (
              <PanelRight className="w-6 h-6" />
            )}
          </Button>
        )}

        {/* Divider */}
        <div className="h-6 w-px bg-gray-300 mx-1" />

        {/* Publish Status */}
        {course && !isCreatingNew && onTogglePublished && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onTogglePublished}
            disabled={isPublishing}
            className={`h-10 px-3 gap-2 ${
              course.isPublished 
                ? 'text-green-600 hover:text-green-700 hover:bg-green-50' 
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            } ${isPublishing ? 'opacity-50 cursor-not-allowed' : ''}`}
            title={course.isPublished ? "Cours publié - Cliquez pour dépublier" : "Cours non publié - Cliquez pour publier"}
          >
            {isPublishing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Globe className="w-4 h-4" />
            )}
            <span className="text-sm font-medium">
              {isPublishing ? "Publication..." : (course.isPublished ? "Dépublier" : "Publier")}
            </span>
          </Button>
        )}

        {/* Divider */}
        <div className="h-6 w-px bg-gray-300 mx-1" />

        {/* Delete Course */}
        {onDeleteCourse && course && !isCreatingNew && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (window.confirm(`Êtes-vous sûr de vouloir supprimer le cours "${course?.title || 'ce cours'}" ? Cette action est irréversible et supprimera tous les modules et blocs associés.`)) {
                onDeleteCourse()
              }
            }}
            className="h-10 w-10 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
            title="Supprimer le cours"
          >
            <Trash2 className="w-6 h-6" />
          </Button>
        )}
      </div>
    </header>
  )
}

